import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'
import { AnimalGenderSchema } from '../schemas'

extendZodWithOpenApi(z)

export const CatBreedDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
  adultWeightMaleAvg: z.number(),
  maleSizeClassification: z.string(),
  adultWeightFemaleAvg: z.number(),
  femaleSizeClassification: z.string(),
})

export type CatBreedDto = z.infer<typeof CatBreedDtoSchema>

export const CatHealthIssueDtoSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  slug: z.string(),
  displayOrder: z.number(),
})

export type CatHealthIssueDto = z.infer<typeof CatHealthIssueDtoSchema>

export const CatFoodAllergenDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
  displayOrder: z.number(),
})

export type CatFoodAllergenDto = z.infer<typeof CatFoodAllergenDtoSchema>

export const CatFoodTypeDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
})

export type CatFoodTypeDto = z.infer<typeof CatFoodTypeDtoSchema>

export const CatDeactivationChildReasonDtoSchema = z.object({
  id: z.string().uuid(),
  description: z.string(),
  displayOrder: z.number(),
  slug: z.string(),
})

export type CatDeactivationChildReasonDto = z.infer<typeof CatDeactivationChildReasonDtoSchema>

export const CatDeactivationReasonDtoSchema = CatDeactivationChildReasonDtoSchema.extend({
  childReasons: CatDeactivationChildReasonDtoSchema.array(),
})

export type CatDeactivationReasonDto = z.infer<typeof CatDeactivationReasonDtoSchema>

export const DeactivateCatBodySchema = z.object({
  primaryReasonId: z.string().uuid(),
  secondaryReasonId: z.string().uuid().nullish(),
  otherReason: z.string().nullish(),
})

export type DeactivateCatBody = z.infer<typeof DeactivateCatBodySchema>

export const CatBodyTypes = {
  UNDERWEIGHT: 'underweight',
  NORMAL: 'normal',
  OVERWEIGHT: 'overweight',
} as const

export const CatBodyTypeSchema = z.nativeEnum(CatBodyTypes)
export type CatBodyType = z.infer<typeof CatBodyTypeSchema>

export const CatActivityLevels = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const

export const CatActivityLevelSchema = z.nativeEnum(CatActivityLevels)
export type CatActivityLevel = z.infer<typeof CatActivityLevelSchema>

export const CatFussinessLevels = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const

export const CatFussinessLevelSchema = z.nativeEnum(CatFussinessLevels)
export type CatFussinessLevel = z.infer<typeof CatFussinessLevelSchema>

export const CatDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  dateOfBirth: z.string().date().nullable(),
  dateOfBirthIsApproximate: z.boolean(),
  dateOfDeath: z.string().date().nullable(),
  dateOfDeathIsApproximate: z.boolean(),
  gender: AnimalGenderSchema.nullable(),
  weight: z.number().nullable(),
  bodyType: CatBodyTypeSchema.nullable(),
  activityLevel: CatActivityLevelSchema.nullable(),
  fussinessLevel: CatFussinessLevelSchema.nullable(),
  recommendedDailyEnergyKCal: z.number().int(),
  minimumDailyEnergyKCal: z.number().int(),
  maximumDailyEnergyKCal: z.number().int(),
  lifestage: z.enum(['kitten', 'adult', 'senior']),
  ageInMonths: z.number(),
  breeds: CatBreedDtoSchema.array(),
  healthIssues: CatHealthIssueDtoSchema.array(),
  foodAllergies: CatFoodAllergenDtoSchema.array(),
  previousFoods: CatFoodTypeDtoSchema.array().optional(),
  deactivatedAt: z.string().datetime().nullish(),
  primaryDeactivationReason: CatDeactivationReasonDtoSchema.omit({ childReasons: true }).nullish(),
  secondaryDeactivationReason: CatDeactivationChildReasonDtoSchema.nullish(),
  otherDeactivationReason: z.string().nullish(),
  updatedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
})

export type CatDto = z.infer<typeof CatDtoSchema>

export const CatPreviewDtoSchema = CatDtoSchema.omit({
  id: true,
})

export type CatPreviewDto = z.infer<typeof CatPreviewDtoSchema>

export const CreateCatBodySchema = z.object({
  name: z.string().optional().openapi({ example: 'Oreo' }),
  dateOfBirth: z.string().date().optional().openapi({ example: '2024-06-01' }),
  dateOfBirthIsApproximate: z.boolean().optional().openapi({ example: false }),
  dateOfDeath: z.string().date().optional().openapi({ example: '2024-06-01' }),
  dateOfDeathIsApproximate: z.boolean().optional().openapi({ example: false }),
  gender: AnimalGenderSchema.optional().openapi({ example: 'male' }),
  weight: z.number().optional().openapi({ example: 4.5 }),
  bodyType: CatBodyTypeSchema.optional().openapi({ example: 'normal' }),
  activityLevel: CatActivityLevelSchema.optional().openapi({ example: 'low' }),
  fussinessLevel: CatFussinessLevelSchema.optional().openapi({ example: 'medium' }),
  breeds: z
    .string()
    .array()
    .optional()
    .openapi({ example: ['bombay'] }),
  healthIssues: z
    .string()
    .array()
    .optional()
    .openapi({ example: ['diabetes', 'anxiety'] }),
  foodAllergies: z
    .string()
    .array()
    .optional()
    .openapi({ example: ['fish-oil', 'fish-gelatine'] }),
  previousFoods: z
    .string()
    .array()
    .optional()
    .openapi({ example: ['dry', 'wet'] }),
})

export type CreateCatBody = z.infer<typeof CreateCatBodySchema>

export const UpdateCatBodySchema = CreateCatBodySchema.partial()

export type UpdateCatBody = z.infer<typeof UpdateCatBodySchema>
